// Pure helpers for the Create-job pages (Watermarks Create, DCP
// Create later). Extracted from `pages/CreateJob.jsx` so the
// validators are testable without mounting the page.
//
// These guard against three classes of input badness:
//   1. Filesystem-illegal chars in filenames (Windows-incompatible
//      paths, control bytes, shell-magic chars).
//   2. SQL/XSS-injection-ish patterns in operator-typed text fields
//      (recipient name, email).
//   3. Email format + RFC length caps (254 total, 64 local-part).
//
// None of these are a substitute for backend validation — they're
// fast-feedback guards so a typo is caught before submit. The
// backend's own validators are the authoritative gate.

// Match characters that break filenames on Windows / NAS shares /
// shell pipelines. Includes control bytes (0x00-0x1f, 0x7f) and
// shell-quoting hazards. Some chars repeat in the union — kept as-
// is to mirror the inline regex in pages/CreateJob.jsx pre-v10.4.32.
//
/* eslint-disable no-control-regex */
export const FILENAME_ILLEGAL = /[<>:"|?*\\/\x00-\x1f\x7f#%&{}\\<>*?/$!'":@+`|=]/;

export function isValidFilename(filename) {
  if (filename == null) return false;
  return !FILENAME_ILLEGAL.test(String(filename));
}

export function sanitizeFilename(filename) {
  if (filename == null) return '';
  return String(filename)
    .replace(/[<>:"|?*\\/\x00-\x1f\x7f#%&{}\\<>*?/$!'":@+`|=]/g, '_')
    .replace(/_+/g, '_')
    .trim();
}

// Operator-typed name (recipient name, project name, etc.). Stricter
// than filename — also rejects `[` `]` since those break URL parsing
// downstream.
//
export const NAME_ILLEGAL = /[<>:"|?*\\/\x00-\x1f\x7f#%&{}\\<>*?/$!'":@+`|=[\]]/;
export const SQL_PATTERNS = /\b(union|select|insert|update|delete|drop|create|alter|exec|execute|script|javascript|eval)\b/i;
export const XSS_PATTERNS = /<script|<iframe|<object|<embed|<link|javascript:|onerror=|onload=|onclick=|onmouseover=/i;

export function isValidName(name) {
  if (name == null) return false;
  const s = String(name);
  return !NAME_ILLEGAL.test(s) && !SQL_PATTERNS.test(s) && !XSS_PATTERNS.test(s);
}

export function sanitizeName(name) {
  if (name == null) return '';
  return String(name)
    .replace(/<[^>]+>/g, '') // Strip HTML tags before per-char filter
    .replace(/[<>:"|?*\\/\x00-\x1f\x7f#%&{}\\<>*?/$!'":@+`|=[\]]/g, '_')
    .replace(/\b(union|select|insert|update|delete|drop|create|alter|exec|execute|script|javascript|eval)\b/gi, '_')
    .replace(/_+/g, '_')
    .trim();
}

// Email validation. Returns `{valid: bool, error: string?}` so
// callers can surface the specific failure reason in helperText.
// A weaker email-only check (RFC-shaped) plus the same SQL/XSS
// guards plus RFC 5321 length caps.
export const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const EMAIL_MAX_LENGTH = 254;        // RFC 5321
export const EMAIL_LOCAL_MAX_LENGTH = 64;   // RFC 5321
const EMAIL_SQL_LITE = /\b(union|select|insert|update|delete|drop|create|alter|exec|execute)\b/i;
const EMAIL_XSS_LITE = /<script|<iframe|javascript:/i;

export function validateEmail(email) {
  if (email == null || String(email).trim() === '') {
    return { valid: false, error: 'Email is required' };
  }
  const s = String(email);
  if (!EMAIL_FORMAT.test(s)) {
    return { valid: false, error: 'Invalid email format' };
  }
  if (EMAIL_SQL_LITE.test(s) || EMAIL_XSS_LITE.test(s)) {
    return { valid: false, error: 'Email contains invalid characters' };
  }
  if (s.length > EMAIL_MAX_LENGTH) {
    return { valid: false, error: 'Email address is too long' };
  }
  const localPart = s.split('@')[0];
  if (localPart.length > EMAIL_LOCAL_MAX_LENGTH) {
    return { valid: false, error: 'Email local part is too long' };
  }
  return { valid: true };
}
